import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  useTheme,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Star as StarIcon, Close as CloseIcon, Refresh as RefreshIcon, BugReport as BugReportIcon } from '@mui/icons-material';
import { useSubscription, SubscriptionTier } from '../../contexts/SubscriptionContext';
import { getAuth } from 'firebase/auth';

interface SubscriptionBannerProps {
  message?: string;
  showUpgrade?: boolean;
}

const SubscriptionBanner: React.FC<SubscriptionBannerProps> = ({ 
  message,
  showUpgrade = true,
}) => {
  const theme = useTheme();
  const { tier, limits, dailyMessageCount, refreshSubscription } = useSubscription();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('annual');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Calculate remaining messages
  const remainingMessages = Math.max(0, limits.dailyMessageLimit - dailyMessageCount);

  // Check subscription status on initial mount
  useEffect(() => {
    const checkSubscription = async () => {
      if (refreshSubscription) {
        try {
          await refreshSubscription();
        } catch (error) {
          console.error('Error refreshing subscription on mount:', error);
        }
      }
    };
    
    checkSubscription();
  }, [refreshSubscription]);

  const handleUpgrade = () => {
    setIsDialogOpen(true);
  };

  const handleRefresh = async () => {
    if (!refreshSubscription) return;
    
    setIsRefreshing(true);
    try {
      await refreshSubscription();
      console.log('Subscription status refreshed');
    } catch (error) {
      console.error('Error refreshing subscription status:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePlanChange = (_event: React.MouseEvent<HTMLElement>, newPlan: 'monthly' | 'annual' | null) => {
    if (newPlan !== null) {
      setSelectedPlan(newPlan);
    }
  };

  const handlePurchase = async () => {
    try {
      console.log('Starting Stripe checkout for plan:', selectedPlan);
      
      // Get the current user's ID token
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be authenticated');
      }
      const idToken = await user.getIdToken();
      console.log('Got user ID token, user ID:', user.uid);

      const requestBody = { planType: selectedPlan };
      console.log('Sending request with body:', JSON.stringify(requestBody));

      // Call the HTTP endpoint
      const response = await fetch('https://us-central1-regs-gpt.cloudfunctions.net/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Received response with status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response data:', errorData);
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Close the dialog before redirecting
      setIsDialogOpen(false);
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error starting checkout:', error);
      alert(`Error starting checkout: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (tier === SubscriptionTier.PREMIUM) {
    return null;
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          bgcolor: theme.palette.army.main,
          border: `1px solid ${theme.palette.army.accent}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" color="white">
            <strong>Free Plan:</strong> {remainingMessages} {remainingMessages === 1 ? 'message' : 'messages'} remaining today
          </Typography>
          <Tooltip title="Refresh subscription status">
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
              sx={{ color: 'white' }}
            >
              {isRefreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          
          {process.env.NODE_ENV === 'development' && (
            <Tooltip title="Debug: Test Payment Success Page">
              <IconButton
                size="small"
                onClick={() => window.location.href = '/payment-success?session_id=test_session_123'}
                sx={{ color: 'yellow' }}
              >
                <BugReportIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setIsDialogOpen(true)}
          startIcon={<StarIcon />}
          sx={{
            bgcolor: theme.palette.army.shield,
            '&:hover': {
              bgcolor: theme.palette.army.accent,
            },
          }}
        >
          Upgrade to Premium
        </Button>
      </Paper>

      <Dialog 
        open={isDialogOpen} 
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Choose Your Premium Plan</Typography>
            <IconButton onClick={() => setIsDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              Unlock unlimited access to Regs-GPT with our premium plans:
            </Typography>
            <Typography variant="body2" color="textSecondary" component="ul" sx={{ pl: 2 }}>
              <li>Unlimited messages</li>
              <li>Advanced search capabilities</li>
              <li>Unlimited bookmarks</li>
              <li>Priority support</li>
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={selectedPlan}
            exclusive
            onChange={handlePlanChange}
            fullWidth
            sx={{ mb: 3 }}
          >
            <ToggleButton 
              value="monthly" 
              sx={{ 
                py: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Typography variant="subtitle1">Monthly</Typography>
              <Typography variant="h6">$9.99/mo</Typography>
            </ToggleButton>
            <ToggleButton 
              value="annual" 
              sx={{ 
                py: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Typography variant="subtitle1">Annual</Typography>
              <Typography variant="h6">$7.99/mo</Typography>
              <Chip 
                label="Save 20%" 
                size="small" 
                color="success"
                sx={{ fontSize: '0.75rem' }}
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handlePurchase}
            startIcon={<StarIcon />}
          >
            {selectedPlan === 'monthly' ? 'Subscribe Monthly' : 'Subscribe Annually'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubscriptionBanner; 